import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  useMapEvents,
} from "react-leaflet";
import { LatLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";
import "./SitesManagementPage.css";
import { useNavigate } from "react-router-dom";
import { safeGetPosition } from "../utils/locationService";
import L from "leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";

const defaultIcon = new L.Icon({
  iconUrl: markerIconPng,
  shadowUrl: markerShadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function LocationMarker({ setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function SitesManagementPage() {
  const [sites, setSites] = useState([]);
  const [nom, setNom] = useState("");
  const [rayon, setRayon] = useState(500);
  const [position, setPosition] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSites();
    // Obtenir la position actuelle
    const getCurrentLocation = async () => {
      try {
        const pos = await safeGetPosition();
        setCurrentLocation([pos.lat, pos.lng]);
        if (mapRef.current) {
          mapRef.current.setView([pos.lat, pos.lng], 16);
        }
      } catch (error) {
        console.error("Erreur de géolocalisation:", error);
      }
    };
    getCurrentLocation();
  }, []);

  useEffect(() => {
    if (sites.length > 0 && mapRef.current) {
      const validSites = sites.filter(site => site.latitude && site.longitude);
      if (validSites.length > 0) {
        const bounds = new LatLngBounds(
          validSites.map((site) => [site.latitude, site.longitude])
        );
        mapRef.current.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }, [sites]);

  const fetchSites = async () => {
    const querySnapshot = await getDocs(collection(db, "sites"));
    const list = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setSites(list);
  };

  const handleSave = async () => {
    if (!nom || !position || !rayon) {
      alert("Remplissez tous les champs et cliquez sur la carte.");
      return;
    }

    const siteData = {
      nom,
      latitude: position[0],
      longitude: position[1],
      rayon: parseInt(rayon),
    };

    if (editingId) {
      await updateDoc(doc(db, "sites", editingId), siteData);
    } else {
      await addDoc(collection(db, "sites"), siteData);
    }

    setNom("");
    setRayon(500);
    setPosition(null);
    setEditingId(null);
    fetchSites();
  };

  const handleEdit = (site) => {
    setNom(site.nom);
    setRayon(site.rayon);
    setPosition([site.latitude, site.longitude]);
    setEditingId(site.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer ce site ?")) {
      await deleteDoc(doc(db, "sites", id));
      fetchSites();
    }
  };

  const handleRecenter = async () => {
    try {
      const pos = await safeGetPosition();
      setCurrentLocation([pos.lat, pos.lng]);
      if (mapRef.current) {
        mapRef.current.setView([pos.lat, pos.lng], 16);
      }
    } catch (error) {
      console.error("Erreur de géolocalisation:", error);
    }
  };

  return (
    <div className="site-page">
      <button className="admin-button" onClick={() => navigate("/admin")}>
        🏠 Espace Admin
      </button>
      <h1>Gestion des sites de travail</h1>

      <div className="form-section">
        <input
          type="text"
          placeholder="Nom du site"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
        />
        <input
          type="number"
          placeholder="Rayon en mètres"
          value={rayon}
          onChange={(e) => setRayon(e.target.value)}
        />
        <button onClick={handleSave}>
          {editingId ? "Mettre à jour" : "Ajouter"}
        </button>
        <button onClick={handleRecenter} className="recenter-button">
          📍 Recentrer sur ma position
        </button>
        <p>Cliquez sur la carte pour définir la position du site</p>
      </div>

      <MapContainer
        ref={mapRef}
        center={currentLocation || [48.8566, 2.3522]}
        zoom={16}
        scrollWheelZoom={false}
        className="map"
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <LocationMarker setPosition={setPosition} />

        {/* Marqueur de position actuelle */}
        {currentLocation && (
          <Marker 
            position={currentLocation}
            icon={L.divIcon({
              className: 'current-location-marker',
              html: '<div class="current-location-dot"></div>',
              iconSize: [12, 12]
            })}
          />
        )}

        {/* Cercle vert = site à ajouter/modifier */}
        {position && !isNaN(parseInt(rayon)) && parseInt(rayon) > 0 && (
          <>
            <Marker position={position} icon={defaultIcon} />
            <Circle
              center={position}
              radius={parseInt(rayon)}
              pathOptions={{ color: "green", fillOpacity: 0.2 }}
            />
          </>
        )}

        {/* Cercles bleus = sites enregistrés */}
        {sites.map((site) => (
          site.latitude && site.longitude && (
            <Circle
              key={site.id}
              center={[site.latitude, site.longitude]}
              radius={site.rayon}
              pathOptions={{ color: "blue", fillOpacity: 0.1 }}
            />
          )
        ))}
      </MapContainer>

      <h2>Sites enregistrés</h2>
      <table className="site-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Coordonnées</th>
            <th>Rayon</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => (
            <tr key={site.id}>
              <td>{site.nom}</td>
              <td>
                {site.latitude && site.longitude 
                  ? `${site.latitude.toFixed(5)}, ${site.longitude.toFixed(5)}`
                  : 'Coordonnées non définies'}
              </td>
              <td>{site.rayon} m</td>
              <td>
                <button onClick={() => handleEdit(site)}>✏️</button>
                <button onClick={() => handleDelete(site.id)}>🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SitesManagementPage;
